import { Facebook, Instagram, Mail, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, MaskLine } from "./Reveal";
import { BRAND, LOCATIONS, SOCIALS } from "../data/site";

const TikTok = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17" {...props}>
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-1.85-2.48V9.78a5.68 5.68 0 1 0 4.94 5.62V8.87A7.35 7.35 0 0 0 20 10.3V7.2a4.3 4.3 0 0 1-3.4-1.38Z" />
  </svg>
);

export const Footer = ({ onBook }) => (
  <footer
    id="contact"
    data-testid="main-footer"
    className="relative overflow-hidden border-t border-white/[0.08] bg-[#080b10]"
  >
    <div className="grain absolute inset-0" />
    <div
      aria-hidden
      className="absolute -left-40 -top-40 h-[440px] w-[440px] rounded-full blur-[140px]"
      style={{
        background: "radial-gradient(circle, rgba(197,160,89,0.16), transparent 70%)",
      }}
    />

    <div className="relative mx-auto max-w-[1400px] px-5 pb-36 pt-20 lg:px-10 lg:py-28">
      <div className="grid gap-14 lg:grid-cols-[1.2fr_1fr_1fr]">
        <div>
          <img
            src={BRAND.logo}
            alt="The Vault Barber Co."
            width="56"
            height="56"
            loading="lazy"
            className="h-14 w-14 object-contain"
          />
          <h2 className="mt-6 font-cond text-3xl font-extrabold uppercase leading-[0.95] sm:text-4xl">
            <MaskLine>Sharpest chair in</MaskLine>
            <MaskLine delay={0.08} className="brass-text">
              Denton County.
            </MaskLine>
          </h2>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-500">
            Locally owned and family run, serving Argyle, Lantana, Bartonville and
            Flower Mound with care and style.
          </p>

          <div className="mt-8 flex gap-3">
            {[
              { Icon: Facebook, href: SOCIALS.facebook, id: "facebook" },
              {
                Icon: Instagram,
                href: SOCIALS.instagramArgyle,
                id: "instagram",
                title: "Instagram · Argyle",
              },
              {
                Icon: Instagram,
                href: SOCIALS.instagramLantana,
                id: "instagram-lantana",
                title: "Instagram · Lantana",
              },
              { Icon: TikTok, href: SOCIALS.tiktok, id: "tiktok" },
            ].map(({ Icon, href, id, title }) => (
              <a
                key={id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={title || id}
                title={title || id}
                data-testid={`footer-social-${id}`}
                className="flex h-11 w-11 items-center justify-center border border-white/12 text-slate-400 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#c5a059] hover:text-[#d8b46b]"
              >
                <Icon size={17} />
              </a>
            ))}
          </div>

          <div className="mt-5 space-y-1.5">
            {LOCATIONS.map((l) => (
              <a
                key={l.id}
                href={l.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-ig-handle-${l.id}`}
                className="flex items-center gap-2 text-xs text-slate-400 transition-colors hover:text-[#d8b46b]"
              >
                <Instagram size={13} className="shrink-0 text-[#c5a059]" />
                <span className="font-cond uppercase tracking-[0.12em]">
                  {l.label}
                </span>
                <span className="text-slate-500">{l.instagramHandle}</span>
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="label">Visit us</p>
          <div className="mt-6 space-y-7">
            {LOCATIONS.map((l) => (
              <div key={l.id}>
                <Link
                  to={`/${l.id}`}
                  data-testid={`footer-location-page-${l.id}`}
                  className="font-cond text-lg font-bold uppercase tracking-[0.1em] text-[#efe7d8] transition-colors hover:text-[#d8b46b]"
                >
                  {l.city}
                </Link>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                  {l.address}
                </p>
                <a
                  href={`tel:${l.tel}`}
                  data-testid={`footer-call-${l.id}`}
                  className="mt-1 inline-block text-sm text-slate-400 transition-colors hover:text-[#d8b46b]"
                >
                  {l.phone}
                </a>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="label">Hours &amp; contact</p>
          <p className="mt-6 font-cond text-2xl font-bold uppercase text-[#efe7d8]">
            {BRAND.hours}
          </p>
          <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-400">
            Walk-ins welcome · Good for kids
          </p>

          <div className="mt-7 space-y-3 text-sm">
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="footer-email"
              className="flex items-center gap-3 text-slate-400 transition-colors hover:text-[#d8b46b]"
            >
              <Mail size={15} className="text-[#c5a059]" />
              {BRAND.email}
            </a>
            <a
              href={`sms:${BRAND.textLine.replace(/\D/g, "")}`}
              data-testid="footer-text-line"
              className="flex items-center gap-3 text-slate-400 transition-colors hover:text-[#d8b46b]"
            >
              <MessageSquare size={15} className="text-[#c5a059]" />
              Text us · {BRAND.textLine}
            </a>
          </div>

          <Reveal delay={0.1}>
            <button
              type="button"
              onClick={onBook}
              data-testid="footer-book-button"
              className="sheen mt-8 w-full bg-[#c5a059] py-4 font-cond text-[13px] font-bold uppercase tracking-[0.22em] text-[#0a0d12] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#d8b46b]"
            >
              <span className="sheen-bar" />
              Book an appointment
            </button>
          </Reveal>
        </div>
      </div>

      <div className="hairline mt-16" />

      <nav
        data-testid="footer-legal-nav"
        className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3"
      >
        {[
          { to: "/terms", label: "Terms of Use", id: "terms" },
          { to: "/privacy", label: "Privacy Policy", id: "privacy" },
          { to: "/cookies", label: "Cookie Policy", id: "cookies" },
        ].map((l) => (
          <Link
            key={l.id}
            to={l.to}
            data-testid={`footer-legal-${l.id}`}
            className="font-cond text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-[#d8b46b]"
          >
            {l.label}
          </Link>
        ))}
        <a
          href="/sitemap.xml"
          data-testid="footer-sitemap-link"
          className="font-cond text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-[#d8b46b]"
        >
          Sitemap
        </a>
      </nav>

      <div className="mt-6 flex flex-col-reverse items-start justify-between gap-6 sm:flex-row sm:items-center">
        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
          © {new Date().getFullYear()} {BRAND.name} · Argyle &amp; Lantana, Texas
        </p>
        <p
          data-testid="footer-designer-credit"
          className="text-[11px] uppercase tracking-[0.16em] text-slate-400"
        >
          Designed by{" "}
          <a
            href="https://mozeid.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-mo-studio-link"
            className="font-semibold text-[#c5a059] underline decoration-[#c5a059]/40 underline-offset-4 transition-colors hover:text-[#d8b46b]"
          >
            Mo Studio
          </a>
        </p>
      </div>
    </div>
  </footer>
);
